import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, FileText, Wallet, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";

export default function ProfessionalDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [apps, setApps] = useState(null);

  const load = () => api.get("/applications/mine").then((r) => setApps(r.data)).catch(() => setApps([]));
  useEffect(() => { load(); }, []);

  const payNow = async (appId) => {
    try {
      const { data } = await api.post("/payments/checkout", { application_id: appId, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch { toast.error("Could not start payment"); }
  };

  const stats = apps ? [
    { icon: FileText, label: "Applications", value: apps.length },
    { icon: Clock, label: "In Progress", value: apps.filter((a) => ["submitted", "in_review", "interview"].includes(a.status)).length },
    { icon: CheckCircle2, label: "Accepted", value: apps.filter((a) => a.status === "accepted").length },
    { icon: Wallet, label: "Fees Paid", value: apps.filter((a) => a.payment_status === "paid").length },
  ] : [];

  return (
    <div>
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Hi {user?.name?.split(" ")[0]} 👋</h1>
        <p className="mt-2 text-muted-foreground">Track your course applications and admission progress.</p>

        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/ /g, "-")}`} className="rounded-2xl border border-border bg-card p-5">
              <s.icon className="h-5 w-5 text-primary" />
              <p className="mt-3 font-mono-nums text-3xl font-extrabold">{s.value}</p>
              <p className="text-sm text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-10 font-heading text-xl font-bold">My Applications</h2>
        {apps === null ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : apps.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-dashed border-border bg-card p-12 text-center">
            <p className="font-heading text-lg font-semibold">No applications yet</p>
            <p className="mt-1 text-sm text-muted-foreground">Explore courses and apply to get started.</p>
            <Button className="mt-4" data-testid="browse-courses-btn" onClick={() => nav("/courses")}>Browse courses</Button>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {apps.map((a) => (
              <div key={a.id} data-testid={`application-${a.id}`} className="overflow-hidden rounded-2xl border border-border bg-card">
                <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center">
                  <img src={a.course?.image_url} alt="" className="h-20 w-full rounded-xl object-cover sm:w-28" />
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-heading text-lg font-bold">{a.course?.title}</h3>
                      <StatusBadge status={a.status} />
                      <StatusBadge status={a.payment_status} />
                    </div>
                    <p className="text-sm text-muted-foreground">{a.course?.specialty} · {a.course?.city}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono-nums text-sm text-muted-foreground">Fee {inr(a.course?.fee)}</p>
                    {a.payment_status !== "paid" && (
                      <Button size="sm" className="mt-2" data-testid={`pay-btn-${a.id}`} onClick={() => payNow(a.id)}>
                        Pay {inr(a.application_fee)}
                      </Button>
                    )}
                  </div>
                </div>
                {/* Timeline */}
                <div className="border-t border-border bg-secondary/40 px-5 py-4">
                  <div className="flex flex-wrap gap-4">
                    {(a.timeline || []).map((t, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="h-2 w-2 rounded-full bg-primary" />
                        <span className="font-semibold capitalize">{t.status.replace("_", " ")}</span>
                        <span className="text-muted-foreground">{t.note}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
